// Mobile-first app shell with navigation

import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import type { Shortcut } from '@/hooks/useKeyboardShortcuts';
import { KeyboardShortcutsHelp } from '@/components/KeyboardShortcutsHelp';
import { OfflineBanner } from '@/components/OfflineBanner';
import { PendingSyncIndicator } from '@/components/PendingSyncIndicator';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import {
  resolveMobileNav,
  getMobileNavItems,
  getMobileHeaderTitle,
} from '@/components/mobileNav';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { cn } from '@/lib/utils';
import {
  Home,
  LayoutDashboard,
  Receipt,
  FileText,
  BarChart3,
  TrendingUp,
  Search,
  Settings,
  LogOut,
  User,
  Shield,
  ListChecks,
  CalendarDays,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useState, useEffect, Suspense } from 'react';
import { useBackstageAccess } from '@/features/backstage/hooks';
import { RouteFallback } from '@/components/RouteFallback';

interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
}

export function Layout() {
  const { t } = useTranslation('common');
  const { user, logout } = useAuth();
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const { context: mobileContext, activeTab: mobileActiveTab } = resolveMobileNav(location.pathname);
  const mobileNavItems = getMobileNavItems(mobileContext, t);
  const mobileHeaderTitle = getMobileHeaderTitle(mobileContext, location.pathname, t);
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);

  const globalShortcuts: Shortcut[] = [
    {
      keys: ['g', 'd'],
      handler: () => navigate('/finance'),
      description: t('keyboard.goToDashboard'),
      group: t('keyboard.navigation'),
    },
    {
      keys: ['g', 'e'],
      handler: () => navigate('/expenses'),
      description: t('keyboard.goToExpenses'),
      group: t('keyboard.navigation'),
    },
    {
      keys: ['g', 'b'],
      handler: () => navigate('/bills'),
      description: t('keyboard.goToBills'),
      group: t('keyboard.navigation'),
    },
    {
      keys: ['g', 'r'],
      handler: () => navigate('/reports'),
      description: t('keyboard.goToReports'),
      group: t('keyboard.navigation'),
    },
    {
      keys: ['g', 's'],
      handler: () => navigate('/search'),
      description: t('keyboard.goToSearch'),
      group: t('keyboard.navigation'),
    },
    {
      keys: ['g', 'l'],
      handler: () => navigate('/lists'),
      description: t('keyboard.goToLists'),
      group: t('keyboard.navigation'),
    },
    {
      keys: ['g', 'a'],
      handler: () => navigate('/agenda'),
      description: t('keyboard.goToAgenda'),
      group: t('keyboard.navigation'),
    },
    {
      keys: '?',
      handler: () => setShowShortcutsHelp(true),
      description: t('keyboard.showHelp'),
      group: t('keyboard.general'),
    },
    {
      keys: 'Escape',
      handler: () => setShowShortcutsHelp(false),
      description: t('keyboard.closeOrDeselect'),
      group: t('keyboard.general'),
      allowInModal: true,
    },
  ];

  useKeyboardShortcuts(globalShortcuts);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const backstageQuery = useBackstageAccess();
  const hasBackstageAccess = backstageQuery.data?.hasAccess ?? false;

  // The home route is a domain hub (launcher); finance/household sections live below.
  const homeItem: NavItem = { path: '/', label: t('nav.home'), icon: Home };

  const financeItems: NavItem[] = [
    { path: '/finance', label: t('nav.dashboard'), icon: LayoutDashboard },
    { path: '/expenses', label: t('nav.expenses'), icon: Receipt },
    { path: '/search', label: t('nav.search'), icon: Search },
    { path: '/bills', label: t('nav.bills'), icon: FileText },
    { path: '/reports', label: t('nav.reports'), icon: BarChart3 },
    { path: '/investments', label: t('nav.investments'), icon: TrendingUp },
  ];

  const householdItems: NavItem[] = [
    { path: '/lists', label: t('nav.lists'), icon: ListChecks },
    { path: '/agenda', label: t('nav.agenda'), icon: CalendarDays },
  ];

  const settingsItem: NavItem = { path: '/settings', label: t('nav.settings'), icon: Settings };
  const backstageItem: NavItem | null = hasBackstageAccess
    ? { path: '/backstage', label: t('nav.backstage'), icon: Shield }
    : null;

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const renderSidebarLink = (item: NavItem) => (
    <Link
      key={item.path}
      to={item.path}
      className={cn(
        'flex items-center gap-3 px-4 py-2 rounded-lg transition-colors',
        isActive(item.path)
          ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
          : 'text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground'
      )}
    >
      <item.icon className="size-5 shrink-0" aria-hidden="true" />
      <span>{item.label}</span>
    </Link>
  );

  const sectionHeader = (label: string) => (
    <p className="px-4 pt-4 pb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
      {label}
    </p>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header
        className={cn(
          'bg-background border-b border-border fixed top-0 left-0 right-0 z-10 lg:pl-64 transition-shadow',
          scrolled && 'shadow-md'
        )}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-xl font-bold text-foreground">
            <span className="lg:hidden">{mobileHeaderTitle}</span>
            <span className="hidden lg:inline">Nomi</span>
          </h1>
          <div className="flex items-center gap-3">
            {/* Touch targets (#406): pad the tap area to ≥44px without growing the
                header row — the negative vertical margin cancels the added padding
                in layout while the padded box still receives taps. Icon sizes are
                unchanged. */}
            <Link
              to="/search"
              className={cn(
                'lg:hidden p-3 -my-2 rounded-md transition-colors',
                isActive('/search')
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              aria-label={t('nav.search')}
            >
              <Search className="size-5" aria-hidden="true" />
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="user-menu-trigger"
                className="flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-md px-3 -my-2 text-sm text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <User className="size-4" aria-hidden="true" />
                <span className="hidden sm:inline">{user?.name}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="flex items-center gap-2">
                    <Settings className="size-4" aria-hidden="true" />
                    {t('nav.settings')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={logout}
                  className="flex items-center gap-2"
                >
                  <LogOut className="size-4" aria-hidden="true" />
                  {t('actions.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-0 bottom-0 w-64 bg-sidebar border-r border-sidebar-border pt-14">
        <div className="px-4 py-4">
          <span className="text-xl font-bold text-foreground">Nomi</span>
        </div>
        <nav className="p-4 space-y-1" data-testid="desktop-sidebar">
          {renderSidebarLink(homeItem)}

          {sectionHeader(t('nav.group.finance'))}
          {financeItems.map(renderSidebarLink)}

          {sectionHeader(t('nav.group.household'))}
          {householdItems.map(renderSidebarLink)}

          <div className="mt-3 pt-3 border-t border-sidebar-border space-y-1">
            {renderSidebarLink(settingsItem)}
            {backstageItem && renderSidebarLink(backstageItem)}
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <main
        className="pb-20 lg:pb-6 lg:pl-64"
        style={{ paddingTop: 'calc(4rem + env(safe-area-inset-top))' }}
      >
        <div className="max-w-7xl mx-auto">
          <OfflineBanner />
          <PendingSyncIndicator />
          {/* Lazy route chunks (issue #396) resolve here so the app shell
              (header, sidebar, bottom nav) stays visible while loading. */}
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </div>
      </main>

      {/* Mobile Bottom Navigation — context-aware (issue #401). The tab set
          swaps by domain; within a domain only the active highlight moves. */}
      <nav
        data-testid="mobile-nav"
        className="lg:hidden fixed bottom-0 left-0 right-0 bg-background border-t border-border z-10"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={mobileContext}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: reduceMotion ? 0 : 0.16 }}
            className="flex gap-1 px-2 py-2"
          >
            {mobileNavItems.map((item) => {
              const active = item.path === mobileActiveTab;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'flex flex-1 flex-col items-center justify-center py-2 rounded-lg transition-colors',
                    active
                      ? 'bg-accent text-accent-foreground'
                      : 'text-muted-foreground hover:bg-accent/50'
                  )}
                >
                  <item.icon className="size-6" aria-hidden="true" />
                  <span className="text-xs mt-1">{item.label}</span>
                </Link>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </nav>

      {/* Keyboard shortcuts hint (desktop only) */}
      <button
        onClick={() => setShowShortcutsHelp(true)}
        className="hidden lg:flex fixed bottom-4 right-4 z-10 items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground bg-card border border-border rounded-lg hover:text-foreground transition-colors"
      >
        <kbd className="px-1.5 py-0.5 text-xs font-mono bg-muted border border-border rounded">?</kbd>
        {t('keyboard.showHelp')}
      </button>

      {/* Keyboard shortcuts help overlay */}
      <KeyboardShortcutsHelp
        isOpen={showShortcutsHelp}
        onClose={() => setShowShortcutsHelp(false)}
        shortcuts={globalShortcuts}
      />
    </div>
  );
}
